import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

type EnergyPhase = 'flow' | 'recovery' | 'management' | 'charging' | 'infrastructure' | 'fleet';

interface EnergyOptions { reducedMotion: boolean; }
interface VideoScrubber { load(): void; setProgress(progress: number): void; }

const PHASES: Array<{ phase: EnergyPhase; start: number }> = [
  { phase: 'flow', start: 0 },
  { phase: 'recovery', start: 0.18 },
  { phase: 'management', start: 0.37 },
  { phase: 'charging', start: 0.5 },
  { phase: 'infrastructure', start: 0.7 },
  { phase: 'fleet', start: 0.88 },
];

function clamp(value: number): number { return Math.min(1, Math.max(0, value)); }
function localProgress(progress: number, start: number, end: number): number {
  return clamp((progress - start) / Math.max(0.001, end - start));
}

function currentPhase(progress: number): EnergyPhase {
  let phase: EnergyPhase = 'flow';
  for (const candidate of PHASES) if (progress >= candidate.start) phase = candidate.phase;
  return phase;
}

function activeVideo(progress: number): number {
  if (progress < 0.18) return 0;
  if (progress < 0.5) return 1;
  if (progress < 0.7) return 2;
  return 3;
}

function createVideoScrubber(video: HTMLVideoElement): VideoScrubber {
  let duration = 0;
  let progress = 0;
  let desiredTime = 0;
  let loaded = false;
  let animationFrame = 0;

  video.muted = true;
  video.playsInline = true;
  video.pause();

  const syncMetadata = (): void => {
    duration = Number.isFinite(video.duration) ? Math.max(0, video.duration - 0.05) : 0;
    desiredTime = duration * progress;
    video.pause();
    if (duration > 0 && video.currentTime === 0) video.currentTime = 0.001;
  };
  video.addEventListener('loadedmetadata', syncMetadata);
  if (video.readyState >= HTMLMediaElement.HAVE_METADATA) syncMetadata();

  const render = (): void => {
    if (duration > 0 && !video.seeking && Math.abs(video.currentTime - desiredTime) > 1 / 48) {
      video.currentTime = desiredTime;
    }
    animationFrame = requestAnimationFrame(render);
  };
  animationFrame = requestAnimationFrame(render);

  document.addEventListener('visibilitychange', () => { if (document.hidden) video.pause(); });
  window.addEventListener('pagehide', () => cancelAnimationFrame(animationFrame), { once: true });

  return {
    load(): void {
      if (loaded) return;
      loaded = true;
      video.preload = 'auto';
      video.load();
    },
    setProgress(nextProgress: number): void {
      progress = clamp(nextProgress);
      if (duration > 0) desiredTime = duration * progress;
    },
  };
}

function setPhaseRail(section: HTMLElement, phase: EnergyPhase): void {
  section.querySelectorAll<HTMLElement>('[data-energy-step]').forEach((item) => {
    item.classList.toggle('is-active', item.dataset.energyStep === phase);
  });
}

function preloadNearSection(section: HTMLElement, scrubbers: VideoScrubber[]): void {
  const observer = new IntersectionObserver((entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    scrubbers[0]?.load();
    scrubbers[1]?.load();
    observer.disconnect();
  }, { rootMargin: '180% 0px 180% 0px' });
  observer.observe(section);
}

export function initEnergySystem({ reducedMotion }: EnergyOptions): void {
  const section = document.querySelector<HTMLElement>('#energy-system');
  const stage = document.querySelector<HTMLElement>('#energy-stage');
  const videos = Array.from(document.querySelectorAll<HTMLVideoElement>('.energy-video'));
  const entryLine = document.querySelector<SVGSVGElement>('#energy-entry-line');
  const flowMap = document.querySelector<SVGSVGElement>('#energy-flow-map');
  const flowMain = document.querySelector<SVGPathElement>('#energy-flow-main');
  const flowBranches = Array.from(document.querySelectorAll<SVGPathElement>('.energy-flow-branch'));
  const flowPulses = Array.from(document.querySelectorAll<SVGCircleElement>('.energy-pulses circle'));
  const intro = document.querySelector<HTMLElement>('#energy-intro');
  const eyebrow = document.querySelector<HTMLElement>('.energy-eyebrow');
  const titleLines = Array.from(document.querySelectorAll<HTMLElement>('.energy-title-mask > span'));
  const introCopy = document.querySelector<HTMLElement>('.energy-intro-copy');
  const flowData = document.querySelector<HTMLElement>('#energy-flow-data');
  const recoveryCopy = document.querySelector<HTMLElement>('#energy-recovery-copy');
  const managementCopy = document.querySelector<HTMLElement>('#energy-management-copy');
  const recoveryOverlay = document.querySelector<SVGSVGElement>('#energy-recovery-overlay');
  const chargeCopy = document.querySelector<HTMLElement>('#energy-charge-copy');
  const chargeLines = Array.from(document.querySelectorAll<HTMLElement>('.energy-charge-mask > span'));
  const chargeData = document.querySelector<HTMLElement>('#energy-charge-data');
  const connectorFlow = document.querySelector<SVGSVGElement>('#energy-connector-flow');
  const infrastructureCopy = document.querySelector<HTMLElement>('#energy-infrastructure-copy');
  const infrastructureLines = Array.from(document.querySelectorAll<HTMLElement>('.energy-infra-mask > span'));
  const networkData = document.querySelector<HTMLElement>('#energy-network-data');
  const hubNetwork = document.querySelector<SVGSVGElement>('#energy-hub-network');
  const fleetStatement = document.querySelector<HTMLElement>('#energy-fleet-statement');
  const finalLines = Array.from(document.querySelectorAll<HTMLElement>('.energy-final-mask > span'));
  const finalAnswer = document.querySelector<HTMLElement>('.energy-final-answer');
  const progressRail = document.querySelector<HTMLElement>('#energy-progress');
  const structuralWipe = document.querySelector<HTMLElement>('#energy-structural-wipe');
  const exitBlackout = document.querySelector<HTMLElement>('#energy-exit-blackout');
  const networkPulse = document.querySelector<HTMLElement>('#energy-network-pulse');
  const vignette = document.querySelector<HTMLElement>('#energy-vignette');
  const atmosphere = document.querySelector<HTMLElement>('#energy-atmosphere');

  if (
    !section || !stage || videos.length !== 4 || !entryLine || !flowMap || !flowMain ||
    flowBranches.length !== 3 || flowPulses.length !== 4 || !intro || !eyebrow || titleLines.length !== 2 ||
    !introCopy || !flowData || !recoveryCopy || !managementCopy || !recoveryOverlay || !chargeCopy ||
    chargeLines.length !== 2 || !chargeData || !connectorFlow || !infrastructureCopy ||
    infrastructureLines.length !== 2 || !networkData || !hubNetwork || !fleetStatement ||
    finalLines.length !== 2 || !finalAnswer || !progressRail || !structuralWipe || !exitBlackout ||
    !networkPulse || !vignette || !atmosphere
  ) return;

  const scrubbers = videos.map(createVideoScrubber);
  preloadNearSection(section, scrubbers);

  if (reducedMotion) {
    stage.style.visibility = 'visible';
    scrubbers[3]?.load();
    scrubbers[3]?.setProgress(0.66);
    section.dataset.energyPhase = 'infrastructure';
    section.dataset.activeVideo = '3';
    return;
  }

  const isMobile = window.matchMedia('(max-width: 760px)').matches;
  let loadTier = 1;

  gsap.set([intro, flowData, recoveryCopy, managementCopy, chargeCopy, chargeData, infrastructureCopy, networkData, fleetStatement, progressRail], { opacity: 0 });
  gsap.set(eyebrow, { opacity: 0, y: 9, letterSpacing: '0.55em' });
  gsap.set(titleLines, { yPercent: 112, y: 0 });
  gsap.set(introCopy, { opacity: 0, y: 11, filter: 'blur(5px)' });
  gsap.set([recoveryCopy, managementCopy], { y: 10 });
  gsap.set(chargeLines, { yPercent: 112, y: 0 });
  gsap.set(infrastructureLines, { yPercent: 112, y: 0 });
  gsap.set(finalLines, { yPercent: 112, y: 0 });
  gsap.set(finalAnswer, { opacity: 0, y: 8 });
  gsap.set(progressRail, { y: 8 });
  gsap.set([flowMap, recoveryOverlay, connectorFlow, hubNetwork, networkPulse], { opacity: 0 });
  gsap.set(entryLine, { opacity: 1 });
  gsap.set(structuralWipe, { opacity: 0, xPercent: -145 });
  gsap.set(exitBlackout, { opacity: 0 });
  gsap.set(videos, { opacity: 0, scale: 1.012, filter: 'brightness(.82) saturate(.88)' });
  gsap.set(videos[0], { opacity: 1 });

  const flowLength = flowMain.getTotalLength();
  gsap.set(flowMain, { strokeDasharray: flowLength, strokeDashoffset: flowLength });
  flowBranches.forEach((branch) => {
    const length = branch.getTotalLength();
    gsap.set(branch, { strokeDasharray: length, strokeDashoffset: length });
  });

  const story = { progress: 0 };
  const syncStory = (): void => {
    const progress = clamp(story.progress);
    const phase = currentPhase(progress);
    const activeIndex = activeVideo(progress);

    scrubbers[0]?.setProgress(localProgress(progress, 0, 0.18));
    scrubbers[1]?.setProgress(localProgress(progress, 0.18, 0.5));
    scrubbers[2]?.setProgress(localProgress(progress, 0.5, 0.7));
    scrubbers[3]?.setProgress(localProgress(progress, 0.7, 0.96));

    if (progress >= 0.24 && loadTier < 2) { loadTier = 2; scrubbers[2]?.load(); }
    if (progress >= 0.52 && loadTier < 3) { loadTier = 3; scrubbers[3]?.load(); }

    section.dataset.storyProgress = progress.toFixed(4);
    section.dataset.activeVideo = String(activeIndex);
    if (section.dataset.energyPhase !== phase) {
      section.dataset.energyPhase = phase;
      setPhaseRail(section, phase);
    }
  };

  const timeline = gsap.timeline({
    defaults: { ease: 'power2.inOut' },
    scrollTrigger: {
      trigger: section,
      start: 'top top',
      end: () => `+=${Math.round(window.innerHeight * (isMobile ? 3.1 : 3.4))}`,
      scrub: isMobile ? 0.28 : 0.48,
      pin: stage,
      pinSpacing: false,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      onEnter: () => { stage.style.visibility = 'visible'; },
      onEnterBack: () => { stage.style.visibility = 'visible'; },
      onLeave: () => { story.progress = 1; syncStory(); },
      onLeaveBack: () => { stage.style.visibility = 'hidden'; story.progress = 0; syncStory(); },
      onRefresh: (self) => {
        section.dataset.scrollStart = self.start.toFixed(2);
        section.dataset.scrollEnd = self.end.toFixed(2);
        if (self.progress > 0) stage.style.visibility = 'visible';
      },
    },
  });

  timeline
    .to(story, { progress: 1, duration: 1, ease: 'none', onUpdate: syncStory }, 0)

    // The exact Section 05 lane direction survives while the road becomes active energy.
    .to(videos[0], { scale: 1, filter: 'brightness(.91) saturate(.86)', duration: 0.08, ease: 'none' }, 0)
    .to(entryLine, { opacity: 0, duration: 0.055 }, 0.035)
    .to(flowMap, { opacity: 1, duration: 0.04 }, 0.045)
    .to(flowMain, { strokeDashoffset: 0, duration: 0.105, ease: 'power2.out' }, 0.045)
    .to(flowBranches, { strokeDashoffset: 0, duration: 0.075, stagger: 0.012 }, 0.09)
    .fromTo(flowPulses, { opacity: 0, y: 82 }, { opacity: 1, y: -58, duration: 0.11, stagger: 0.012, ease: 'none' }, 0.065)
    .to(intro, { opacity: 1, duration: 0.03 }, 0.045)
    .to(eyebrow, { opacity: 1, y: 0, letterSpacing: '0.34em', duration: 0.055 }, 0.048)
    .to(titleLines, { yPercent: 0, y: 0, duration: 0.065, stagger: 0.013, ease: 'power3.out' }, 0.055)
    .to(introCopy, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.045 }, 0.084)
    .to(flowData, { opacity: 1, duration: 0.035 }, 0.095)
    .to(progressRail, { opacity: 1, y: 0, duration: 0.04 }, 0.1)
    .to([intro, flowData], { opacity: 0, y: -8, duration: 0.025 }, 0.155)

    // Abstract line returns to the truck's rear axle through a dark energy-path wipe.
    .to(structuralWipe, { opacity: 1, xPercent: 145, duration: 0.045 }, 0.16)
    .set(videos[0], { opacity: 0 }, 0.18)
    .set(videos[1], { opacity: 1, scale: 1.012, filter: 'brightness(.83) saturate(.85)' }, 0.18)
    .to([flowMap, structuralWipe], { opacity: 0, duration: 0.025 }, 0.185)
    .to(recoveryCopy, { opacity: 1, y: 0, duration: 0.04 }, 0.205)
    .to(recoveryOverlay, { opacity: 0.7, duration: 0.04 }, 0.23)
    .to(atmosphere, { opacity: 0.24, x: '1vw', duration: 0.12, ease: 'none' }, 0.22)
    .to(recoveryCopy, { opacity: 0, y: -8, duration: 0.025 }, 0.335)

    // Same physical vehicle, tighter underbody framing for predictive management.
    .to(videos[1], { scale: 1.075, xPercent: 2.5, filter: 'brightness(.72) saturate(.8)', duration: 0.13, ease: 'none' }, 0.37)
    .to(recoveryOverlay, { opacity: 0.34, duration: 0.04 }, 0.37)
    .to(managementCopy, { opacity: 1, y: 0, duration: 0.04 }, 0.385)
    .to(managementCopy, { opacity: 0, y: -8, duration: 0.025 }, 0.465)
    .to(recoveryOverlay, { opacity: 0, duration: 0.025 }, 0.475)

    // Energy path becomes the charging hub's physical connector.
    .to(structuralWipe, { opacity: 1, xPercent: -145, duration: 0 }, 0.47)
    .to(structuralWipe, { xPercent: 145, duration: 0.05 }, 0.47)
    .set(videos[1], { opacity: 0 }, 0.5)
    .set(videos[2], { opacity: 1, scale: 1.012, filter: 'brightness(.8) saturate(.84)' }, 0.5)
    .to(structuralWipe, { opacity: 0, duration: 0.025 }, 0.505)
    .to(chargeCopy, { opacity: 1, duration: 0.035 }, 0.52)
    .to(chargeLines, { yPercent: 0, y: 0, duration: 0.06, stagger: 0.013, ease: 'power3.out' }, 0.525)
    .to(chargeData, { opacity: 1, duration: 0.035 }, 0.56)
    .to(connectorFlow, { opacity: 0.72, duration: 0.04 }, 0.575)
    .fromTo('#energy-connector-flow circle', { x: 54, opacity: 0 }, { x: -36, opacity: 1, duration: 0.09, stagger: 0.013, ease: 'none' }, 0.575)
    .to([chargeCopy, chargeData, connectorFlow], { opacity: 0, y: -8, duration: 0.025 }, 0.665)

    // Charging-bay column conceals the rise into the complete energy hub.
    .to(structuralWipe, { opacity: 1, xPercent: -145, duration: 0 }, 0.665)
    .to(structuralWipe, { xPercent: 145, duration: 0.05 }, 0.665)
    .set(videos[2], { opacity: 0 }, 0.7)
    .set(videos[3], { opacity: 1, scale: 1.02, filter: 'brightness(.76) saturate(.82)' }, 0.7)
    .to(structuralWipe, { opacity: 0, duration: 0.025 }, 0.705)
    .to(videos[3], { scale: 1, filter: 'brightness(.9) saturate(.86)', duration: 0.16, ease: 'none' }, 0.7)
    .to(infrastructureCopy, { opacity: 1, duration: 0.035 }, 0.72)
    .to(infrastructureLines, { yPercent: 0, y: 0, duration: 0.06, stagger: 0.013, ease: 'power3.out' }, 0.725)
    .to(networkData, { opacity: 1, duration: 0.035 }, 0.75)
    .to(hubNetwork, { opacity: 0.55, duration: 0.05 }, 0.765)
    .to([infrastructureCopy, networkData], { opacity: 0, y: -8, duration: 0.025 }, 0.85)

    // Multiple vehicles resolve into one coordinated fleet energy system.
    .to(fleetStatement, { opacity: 1, duration: 0.035 }, 0.875)
    .to(finalLines, { yPercent: 0, y: 0, duration: 0.055, stagger: 0.012, ease: 'power3.out' }, 0.88)
    .to(finalAnswer, { opacity: 1, y: 0, duration: 0.025 }, 0.895)
    .to([fleetStatement, progressRail, hubNetwork], { opacity: 0, y: -7, duration: 0.025 }, 0.955)

    // A warm physical energy pulse leaves the hub and cools into Section 07's data heartbeat.
    .to(exitBlackout, { opacity: 1, duration: 0.035 }, 0.96)
    .to(networkPulse, { opacity: 1, duration: 0.025 }, 0.972)
    .fromTo('#energy-network-pulse span', { scale: 0.45 }, { scale: 2.2, duration: 0.005, repeat: 3, yoyo: true, ease: 'power2.inOut' }, 0.98)
    .to(vignette, { opacity: 0.82, duration: 0.025 }, 0.975);

  requestAnimationFrame(() => ScrollTrigger.refresh());
}
